import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import Spinner from "../../components/ui/Spinner";
import { rememberPurchaseToken } from "../../services/household";

/**
 * Where a purchase email's link lands.
 *
 * **Why this route exists rather than linking straight to Auth0.** The link carries a
 * signed token that has to survive a round trip through Auth0's signup and be presented
 * afterwards. Auth0 has nowhere to keep it, so the link points here instead: stash the
 * token, send them to sign up, and `HouseholdGate` spends it when they come back.
 *
 * It is also the only way to create an account anywhere in this product. Both clients
 * call `loginWithRedirect()` with no `screen_hint`, so Auth0 shows its sign-IN form and
 * a new customer has nothing to click. This passes `screen_hint: "signup"`.
 *
 * **The token is a convenience, not a ticket.** Somebody who opens this page without one
 * is sent to sign up exactly the same way — and that is the honest behaviour, since
 * anybody may create an account whether or not they have bought anything.
 */
export default function WelcomePage() {
  const [params] = useSearchParams();
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();

  const token = params.get("t");

  useEffect(() => {
    if (isLoading) return;

    // Stashed before the redirect, because the redirect replaces this page and there is
    // no "after" here to do it in.
    if (token) rememberPurchaseToken(token);

    if (isAuthenticated) {
      // Already signed in -- a second visit, or somebody who followed the link in a
      // browser where they have a session. Sending them through signup would be
      // nonsense, so send them in; the gate reads the token from storage either way.
      window.location.replace("/");
      return;
    }

    loginWithRedirect({
      authorizationParams: {
        // The reason this page exists in product terms: without it Auth0 shows the
        // sign-IN form, and a customer who has never had an account has nothing to
        // click.
        screen_hint: "signup",
      },
    });
  }, [isLoading, isAuthenticated, token, loginWithRedirect]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-white">
      <Spinner size={64} />
      <p className="text-ink-3 text-sm">Taking you to sign up…</p>
    </div>
  );
}
